import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'react-toastify';
import { chatbotAdminApi } from '../../api/chatbotApi';
import type { ChatbotBotInstance } from '../../types/chatbot.types';
import { TenantAiKeysCard } from '../../components/admin/TenantAiKeysCard';
import { BotInfoFieldsQuickDialog } from '../../components/admin/BotInfoFieldsQuickDialog';
import { useConfirm } from '@/contexts/confirmContext';

export function ChatbotBotsPage() {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [bots, setBots] = useState<ChatbotBotInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [infoFieldsBotId, setInfoFieldsBotId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (bot: ChatbotBotInstance) => {
    const confirmed = await confirm({
      title: 'Eliminar bot',
      message: `Se elimina «${bot.name}» junto con sus conversaciones y el vínculo con las instancias. No se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }
    setDeletingId(bot.botId);
    try {
      await chatbotAdminApi.deleteIndependentBot(bot.botId);
      setBots(current => current.filter(item => item.botId !== bot.botId));
      toast.success('Bot eliminado');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al eliminar bot';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await chatbotAdminApi.listAllBots();
        setBots(data);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Error al cargar bots';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className='container mx-auto py-6 space-y-6'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <Button variant='ghost' size='icon' onClick={() => navigate('/admin/chatbot')}>
            <ArrowLeft className='h-4 w-4' />
          </Button>
          <div>
            <h1 className='text-2xl font-bold flex items-center gap-2'>
              <Bot className='h-6 w-6' />
              Bots IA
            </h1>
            <p className='text-muted-foreground'>
              Configura modelos, prompts y memoria de sesión para cada bot.
            </p>
          </div>
        </div>
        <Button onClick={() => navigate('/admin/chatbot/bots/new')}>
          <Plus className='h-4 w-4 mr-2' />
          Nuevo bot
        </Button>
      </div>

      <TenantAiKeysCard title='API keys del tenant (fallback para bots sin key propia)' />

      <Card>
        <CardHeader>
          <CardTitle>Bots registrados</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className='text-muted-foreground'>Cargando...</p>
          ) : bots.length === 0 ? (
            <p className='text-muted-foreground'>No hay bots configurados.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Estado IA</TableHead>
                  <TableHead>Conversaciones</TableHead>
                  <TableHead className='text-right'>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bots.map(bot => (
                  <TableRow key={bot.botId}>
                    <TableCell className='font-medium'>{bot.name}</TableCell>
                    <TableCell>{bot.modelId || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={bot.aiReady ? 'default' : 'secondary'}>
                        {bot.aiReady ? 'Lista' : 'Pendiente'}
                      </Badge>
                    </TableCell>
                    <TableCell>{bot.conversationsCount ?? 0}</TableCell>
                    <TableCell className='text-right space-x-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setInfoFieldsBotId(bot.botId)}
                      >
                        Info / aviso
                      </Button>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => navigate(`/admin/chatbot/bots/${bot.botId}`)}
                      >
                        Configurar
                      </Button>
                      <Button
                        variant='destructive'
                        size='sm'
                        disabled={deletingId === bot.botId}
                        onClick={() => void handleDelete(bot)}
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <BotInfoFieldsQuickDialog
        botId={infoFieldsBotId}
        open={Boolean(infoFieldsBotId)}
        onOpenChange={open => {
          if (!open) setInfoFieldsBotId(null);
        }}
      />
    </div>
  );
}
